import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProgressLoadingComponent } from 'projects/ui-library/src/lib/components/loading/progress-loading/progress-loading.component';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Position } from 'src/app/interfaces/shared/position';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { ButtonComponent } from 'ui-library';

const MAX_ACCURACY = 4;
const GOOD_ACCURACY = 5;
const LOW_ACCURACY = 2;
const BAD_ACCURACY = 1;
const NO_ACCURACY = 0;

@Component({
  selector: 'app-tracker-position',
  templateUrl: './tracker-position.component.html',
  styleUrls: ['./tracker-position.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    ProgressLoadingComponent,
    ButtonComponent
  ]
})
export class TrackerPositionComponent implements OnInit, OnDestroy {

  @Input() public unit: any = {
    lastPosition: {
      lat: 0,
      lon: 0,
      x: 0,
      y: 0,
      z: 0
    }
  };
  @Input() public trackers: any[] = [];
  @Input() accuracy: number = MAX_ACCURACY;
  @Input() duration: number = 10;
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();
  @Output() located: EventEmitter<any> = new EventEmitter<any>();
  

  // Signals
  trackerId = signal<string>('');
  trackerPosition = signal<Position[]>([]);
  status = signal<any>('stale');
  messageDisplayed = signal<any>('');
  showMessage = signal<boolean>(false);
  trackerName = signal<string>('');
  dataObservableArray = signal<any[]>([]);
  isFromTracker = signal<boolean>(false);
  lastPositions = signal<Record<string, any>>({});
  resetLoading = signal<boolean>(false);
  lastPosition = signal<Position>({
    lat: 0,
    lon: 0,
    x: 0,
    y: 0,
    z: 0
  });
  lowQualityReceived = signal<boolean>(false);
  isDialog = signal<boolean>(false);
  timeouts = signal<any[]>([]);

  // Computed values
  hasTrackerData = computed(() => this.trackerPosition().length > 0);
  isLowQualityData = computed(() => this.lowQualityReceived());
  canSubmit = computed(() => this.lastPosition()?.lat && this.lastPosition()?.lon);

  // Getters for template binding
  get trackerIdValue(): string {
    return this.trackerId();
  }
  set trackerIdValue(value: string) {
    this.trackerId.set(value);
  }

  get lastPositionValue(): any {
    return this.lastPosition();
  }
  set lastPositionValue(value: any) {
    this.lastPosition.set(value);
  }

  MIN_QUALITY = 3;
  destroy$ = new Subject<void>();
  socketSub: Subscription | null = null;

  constructor(
    private webSocket: WebSocketService,
    private translate: TranslateService,
    @Optional() public dialogRef: MatDialogRef<TrackerPositionComponent>, 
    @Optional() @Inject(MAT_DIALOG_DATA) public _data: any,
    private unitService: UnitService
  ) {
    if (this._data) {
      this.isDialog.set(true);
      this.unit = this._data.unit || this.unit;
      this.trackers = this._data.trackers || this.trackers;
      this.trackers.sort((a, b) => a.name > b.name ? 1 : -1);
      this.accuracy = this._data.accuracy;
      this.duration = this._data.duration;
      if (this.unit)
        this.lastPosition.set({ ...this.unit.lastPosition });
    }

    // Effect to handle position changes
    effect(() => {
      const position = this.lastPosition();
      if (!this.isDialog() && position?.lat && position?.lon) {
        this.located.emit(position);
      }
    });
  }

  ngOnInit(): void {
    if (!this.isDialog() && this.trackers.length)
      this.trackers.sort((a, b) => a.name > b.name ? 1 : -1);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelTimeouts();
  }

  handleClose(lastPosition = null) {
    if (this.isDialog()) {
      this.dialogRef.close(lastPosition);
    } else
      this.close.emit();
  }

  getPosFromTracker(e: any = null) {
    this.cancelTimeouts();  
    this.resetLoading.update(loading => !loading);
    this.showMessage.set(false);
    
    if (e) {
      this.trackerId.set(e.value);
      this.trackers.forEach(t => {
        if (t.id == e.value) this.trackerName.set(t.name);
      });
      const currentLastPositions = this.lastPositions();
      if (currentLastPositions[this.trackerId()]) {
        if (currentLastPositions[this.trackerId()].error) {
          this.status.set(currentLastPositions[this.trackerId()].status);
          this.messageDisplayed.set(currentLastPositions[this.trackerId()].error);
          this.showMessage.set(true);
          if (this.unit)
            this.lastPosition.set({ ...this.unit.lastPosition });
        } else {
          this.lastPosition.set(currentLastPositions[this.trackerId()]);
          this.status.set("ok");
          this.messageDisplayed.set(this.translate.instant('units.tracker_pos.success'));
          this.showMessage.set(true);
          if (!this.isDialog())
            this.located.emit(this.lastPosition());
        }
        return;
      }
    }

    if (this.trackerId()) {
      this.trackerPosition.set([]);
      this.isFromTracker.set(true);
      this.showMessage.set(true);
      this.status.set('loading');

      this.lowQualityReceived.set(false);
      this.messageDisplayed.set(this.translate.instant('units.tracker_pos.waiting_data'));
      
      this.socketSub = this.webSocket.getUpdate("Track").pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
        this.dataObservableArray.set(data);
        data.forEach(async (t: any) => {
          if ((t.tracker.id || t.tracker) == this.trackerId()) {

            if ((t.message?.accuracy == undefined || !this.isLowQuality(t.message.accuracy)) && !this.lowQualityReceived()) {
              this.messageDisplayed.set(this.translate.instant('units.tracker_pos.calculating'))
              // Position calculation
              this.trackerPosition.update(positions => [...positions, t]);
            } else if (t.message?.accuracy || this.isLowQuality(t.message?.accuracy)) {
              this.lowQualityReceived.set(true);
              this.trackerPosition.set([]); // need to have all tracks of sufficient quality
            }
          }
        })
      })

      for (let i = 1; i < 4; i++) {
        const timeout = setTimeout(() => {
          if (this.trackerPosition().length) {
            this.calculatePosition();
            this.cancelTimeouts();
          } else if (this.lowQualityReceived() && i < 3) {
            this.status.set('loading');
            this.messageDisplayed.set(this.translate.instant(`units.tracker_pos.wait${i == 1 ? '' : "_again"}_better_quality`));
            this.lowQualityReceived.set(false);
            this.resetLoading.update(loading => !loading);
          } else if (this.lowQualityReceived() && i == 3) {
            this.messageDisplayed.set(this.translate.instant('units.tracker_pos.low_quality_data' + (this.accuracy != LOW_ACCURACY ? '_bis':'')));
            this.status.set("retry");
            this.lastPositions.update(positions => ({
              ...positions,
              [this.trackerId()]: { error: this.messageDisplayed(), status: this.status() }
            }));
            this.cancelTimeouts();
          } else if (!this.trackerPosition().length) {
            this.status.set('error');
            this.isFromTracker.set(false);

            this.messageDisplayed.set(`${this.translate.instant('units.tracker_pos.no_data')}`);

            this.lastPositions.update(positions => ({
              ...positions,
              [this.trackerId()]: { error: this.messageDisplayed(), status: this.status() }
            }));
            this.cancelTimeouts();
          }
        }, this.duration * i * 1000);
        
        this.timeouts.update(timeouts => [...timeouts, timeout]);
      }
    }
  }

  isLowQuality(accuracy: any) {
    if (accuracy == undefined) return false;
    switch(accuracy) {
      case MAX_ACCURACY:
        return false;
      case GOOD_ACCURACY: 
        return this.accuracy == GOOD_ACCURACY || this.accuracy == LOW_ACCURACY ? false : true;
      case LOW_ACCURACY:
        return this.accuracy == LOW_ACCURACY ? false : true;
      case BAD_ACCURACY:
      case NO_ACCURACY:
      default:
        return true;
    }
  }

  cancelTimeouts() {
    this.timeouts().forEach(t => clearTimeout(t));
    this.timeouts.set([]);
    if (this.socketSub) {
      this.socketSub.unsubscribe();
      this.socketSub = null;
    }
  }

  calculatePosition() {
    this.status.set('ok');
    const currentTrackerPosition = this.trackerPosition();
    const position = currentTrackerPosition.reduce((lastPosition, track) => {
      return {
        x: lastPosition.x + (!isNaN(track.x) ? track.x : 0),
        y: lastPosition.y + (!isNaN(track.y) ? track.y : 0),
        z: lastPosition.z + (!isNaN(track.z) ? track.z : 0),
        lon: lastPosition.lon + (!isNaN(track.lon) ? track.lon : 0),
        lat: lastPosition.lat + (!isNaN(track.lat) ? track.lat : 0)
      };
    }, { lat: 0, lon: 0, x: 0, y: 0, z: 0 });

    let calculatedPosition = {
      lon: parseFloat((position.lon / currentTrackerPosition.length).toFixed(7)),
      lat: parseFloat((position.lat / currentTrackerPosition.length).toFixed(7)),
      x: parseFloat((position.x / currentTrackerPosition.length).toFixed(2)),
      y: parseFloat((position.y / currentTrackerPosition.length).toFixed(2)),
      z: parseFloat((position.z / currentTrackerPosition.length).toFixed(2)),
    };
    
    this.lastPositions.update(positions => ({
      ...positions,
      [this.trackerId()]: calculatedPosition
    }));
    this.lastPosition.set({ ...calculatedPosition });
    this.messageDisplayed.set(this.translate.instant('units.tracker_pos.success'));
    if (!this.isDialog())
      this.located.emit({ ...this.lastPosition() });
  }

  handleSubmit() {
    if (this.isDialog()) {
      let unit = { ...this.unit }
      unit.picture = unit.icon;
      if (unit.id && this.lastPosition()) {
        let unitPayload = { id: unit.id, lastPosition: this.lastPosition() }
        unit.lastPosition = this.lastPosition();
        this.unitService.updateUnit(unitPayload).pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.handleClose(unit.lastPosition);
        },
          err => {
            console.error(err)
          })
      }
    } else
      this.submit.emit(this.lastPosition());
  }

  locationChange(pos: any) {
    if (!this.isDialog() && this.lastPosition()?.lat && this.lastPosition()?.lon)
      this.located.emit(this.lastPosition());
  }
}
